import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { Link, navigate, useTitle } from '../../router';
import { useAuth } from '../../lib/auth';
import { Mascot } from './Mascot';

/* "Try without an account": /try (also linked from the home page, the login page and the schools page).
   One click creates a private 24-hour trial teacher (with the example class) or trial student (in an example class)
   — see server/trials.js. TrialBanner is the strip shown at the top while a trial account is signed in. */

type L3 = { en: string; ro: string; tr: string };
const X = (en: string, ro: string, tr: string): L3 => ({ en, ro, tr });
const TX = {
  title: X('Try ChemAI without an account', 'Încearcă ChemAI fără cont', 'ChemAI’yi hesapsız dene'),
  lead: X('One click gives you a private trial account for 24 hours. Nothing to fill in.', 'Un clic îți dă un cont de probă privat pentru 24 de ore. Nu completezi nimic.', 'Tek tıkla 24 saatlik, sana özel bir deneme hesabı. Hiçbir şey doldurmazsın.'),
  teacher: X('Try as a teacher', 'Încearcă ca profesor', 'Öğretmen olarak dene'),
  teacherD: X('An example class with students, results, analytics and a finished live game is ready. Make a quiz, give homework, start a live game on the projector.', 'Te așteaptă o clasă exemplu cu elevi, rezultate, analize și un concurs live încheiat. Creează un test, dă o temă, pornește un concurs live pe proiector.', 'Öğrencileri, sonuçları, analizi ve bitmiş bir canlı yarışmasıyla örnek bir sınıf hazır. Test oluştur, ödev ver, projeksiyonda canlı yarışma başlat.'),
  student: X('Try as a student', 'Încearcă ca elev', 'Öğrenci olarak dene'),
  studentD: X('You are in an example class with homework, a short test in test mode, a lab task and a project — plus the lab, games and Prof. AKAY.', 'Ești într-o clasă exemplu cu o temă, un test scurt în mod test, o sarcină de laborator și un proiect — plus laboratorul, jocurile și Prof. AKAY.', 'Ödevi, sınav modunda kısa bir sınavı, laboratuvar görevi ve projesi olan örnek bir sınıftasın — ayrıca laboratuvar, oyunlar ve Prof. AKAY.'),
  rules: X('The trial account and everything in it are deleted after 24 hours. It cannot send e-mails or connect Google, and real students cannot join its classes. To keep your work, create a free account.', 'Contul de probă și tot ce conține se șterg după 24 de ore. Nu poate trimite e-mailuri și nu se conectează la Google, iar elevii reali nu pot intra în clasele lui. Ca să-ți păstrezi munca, creează un cont gratuit.', 'Deneme hesabı ve içindeki her şey 24 saat sonra silinir. E-posta gönderemez, Google’a bağlanamaz; gerçek öğrenciler sınıflarına katılamaz. Çalışmanı saklamak için ücretsiz hesap aç.'),
  signedIn: X('You are signed in with your account. Sign out first if you want to see a trial account.', 'Ești autentificat cu contul tău. Ieși mai întâi dacă vrei să vezi un cont de probă.', 'Kendi hesabınla giriş yaptın. Deneme hesabını görmek istersen önce çıkış yap.'),
  busy: X('Preparing your trial…', 'Pregătim contul de probă…', 'Deneme hesabın hazırlanıyor…'),
  limit: X('Too many trial accounts from this network right now. Please try again in an hour — or create a free account.', 'Prea multe conturi de probă din această rețea acum. Încearcă peste o oră — sau creează un cont gratuit.', 'Şu an bu ağdan çok fazla deneme hesabı açıldı. Bir saat sonra tekrar dene — ya da ücretsiz hesap aç.'),
  full: X('Trial accounts are full for today. Please create a free account instead.', 'Conturile de probă s-au terminat pentru azi. Creează un cont gratuit.', 'Bugünlük deneme hesapları doldu. Bunun yerine ücretsiz hesap aç.'),
  error: X('Could not create the trial. Please try again.', 'Nu s-a putut crea contul de probă. Încearcă din nou.', 'Deneme hesabı açılamadı. Lütfen tekrar dene.'),
  account: X('I want a real account', 'Vreau un cont real', 'Gerçek hesap açmak istiyorum'),
  logout: X('Sign out', 'Ieși', 'Çıkış yap'),
};

export function TrialPage() {
  const { lang } = useI18n();
  const L = (o: L3) => (o as Record<string, string>)[lang] ?? o.en;
  const { user, ready, logout } = useAuth();
  useTitle(L(TX.title));
  const [busy, setBusy] = useState<'teacher' | 'student' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const start = async (role: 'teacher' | 'student') => {
    setBusy(role); setErr(null);
    try {
      const r = await fetch('/api/v1/trial', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role, lang }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const code = j?.error?.code;
        setErr(L(code === 'RATE_LIMITED' ? TX.limit : code === 'TRIALS_FULL' ? TX.full : code === 'ALREADY_SIGNED_IN' ? TX.signedIn : TX.error));
        setBusy(null); return;
      }
      // full reload so the whole app picks up the new session
      window.location.assign(j.data.home || '/');
    } catch { setErr(L(TX.error)); setBusy(null); }
  };
  const realUser = !!user && !user.trial && !user.guest;
  return (
    <div className="page">
      <style>{`
        .try-head{display:flex;gap:16px;align-items:center}
        .try-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:14px;margin-top:16px}
        .try-card{display:flex;flex-direction:column;gap:10px;text-align:left;font:inherit;color:inherit;cursor:pointer;padding:18px 20px;border-radius:16px;border:1.5px solid var(--line,#e3ddcc);background:var(--card,#fffdf7)}
        .try-card:hover:not(:disabled),.try-card:focus-visible{border-color:var(--accent,#1f6f5c)}
        .try-card:disabled{opacity:.6;cursor:wait}
        .try-card .ico{font-size:40px;line-height:1}
        .try-card b{font-size:1.25rem}
        .try-card .go{margin-top:auto;font-weight:700;color:var(--accent,#1f6f5c)}
        @media(max-width:560px){.try-head .m{display:none}}
      `}</style>
      <header className="page-head try-head">
        <span className="m" aria-hidden="true"><Mascot mood="wave" size={72} /></span>
        <div><h1 style={{ margin: 0 }}>🧪 {L(TX.title)}</h1><p className="lead" style={{ margin: '6px 0 0' }}>{L(TX.lead)}</p></div>
      </header>
      {ready && realUser ? (
        <section className="panel">
          <p style={{ marginTop: 0 }}>{L(TX.signedIn)}</p>
          <button type="button" className="btn" onClick={() => void logout()}>{L(TX.logout)}</button>
        </section>
      ) : (
        <div className="try-grid">
          <button type="button" className="try-card" disabled={busy !== null} onClick={() => void start('teacher')}>
            <span className="ico" aria-hidden="true">👩‍🏫</span>
            <b>{L(TX.teacher)}</b>
            <span className="muted">{L(TX.teacherD)}</span>
            <span className="go">{busy === 'teacher' ? L(TX.busy) : `${L(TX.teacher)} →`}</span>
          </button>
          <button type="button" className="try-card" disabled={busy !== null} onClick={() => void start('student')}>
            <span className="ico" aria-hidden="true">🧑‍🎓</span>
            <b>{L(TX.student)}</b>
            <span className="muted">{L(TX.studentD)}</span>
            <span className="go">{busy === 'student' ? L(TX.busy) : `${L(TX.student)} →`}</span>
          </button>
        </div>
      )}
      {err && <p className="error" role="alert">{err}</p>}
      <p className="muted small" style={{ marginTop: 16 }}>ℹ️ {L(TX.rules)}</p>
      <p><Link to="/login">{L(TX.account)} →</Link></p>
    </div>
  );
}

/** Strip at the top of every page while a trial account is signed in: time left, "create a free account", sign out. */
export function TrialBanner() {
  const { lang } = useI18n();
  const { user, logout } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 60_000); return () => window.clearInterval(t); }, []);
  if (!user?.trial || !user.trialEndsAt) return null;
  const T = (en: string, ro: string, tr: string) => (lang === 'ro' ? ro : lang === 'tr' ? tr : en);
  const ms = Math.max(0, user.trialEndsAt - now);
  const h = Math.floor(ms / 3600e3); const m = Math.floor((ms % 3600e3) / 60e3);
  const left = h > 0 ? T(`${h} h ${m} min left`, `încă ${h} h ${m} min`, `${h} sa ${m} dk kaldı`) : T(`${m} min left`, `încă ${m} min`, `${m} dk kaldı`);
  const real = async () => { await logout(); navigate(`/login?tab=${user.role === 'teacher' ? 'teacher' : 'student'}`); };
  return (
    <div className="trial-banner" role="status">
      <span>🧪 <b>{user.role === 'teacher' ? T('Trial teacher account', 'Cont de probă (profesor)', 'Deneme öğretmen hesabı') : T('Trial student account', 'Cont de probă (elev)', 'Deneme öğrenci hesabı')}</b> · {T('deleted after 24 hours', 'se șterge după 24 de ore', '24 saat sonra silinir')} · {left}</span>
      <span className="trial-actions">
        <button type="button" className="btn btn-small btn-primary" onClick={() => void real()}>{T('Create a free account', 'Creează un cont gratuit', 'Ücretsiz hesap aç')}</button>
        <button type="button" className="btn btn-small btn-ghost" onClick={() => void logout().then(() => navigate('/'))}>{T('Sign out', 'Ieși', 'Çıkış')}</button>
      </span>
    </div>
  );
}
